import { useState } from "react";
import {
    KickLogo,
    type KickLogoColor,
    type KickLogoShape,
    type KickLogoSize,
    type KickLogoVariant,
} from "@design-system/KickLogo/KickLogo";
import { kickLogoAnatomyVariantId } from "./componentAnatomyKickLogo";
import { useAnatomyMode } from "./componentAnatomyMode";
import {
    kickLogoColors,
    kickLogoFormatProps,
    kickLogoShapes,
    kickLogoSizePixels,
    kickLogoSizes,
    kickLogoVariants,
    toKickLogoFormat,
} from "./KickLogoPlayground.lib";
import {
    KickLogoSizeMatrix,
    KickLogoVariantMatrix,
} from "./KickLogoPlaygroundMatrix";
import { BooleanControl, OptionToggle } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./KickLogoPlayground.module.scss";

/** Ready-components playground for `KickLogo`: the Figma KickLogo and KickIcon sets, with the x-ray anatomy. */
export const KickLogoPlayground = () => {
    const [variant, setVariant] = useState<KickLogoVariant>("full");
    const [shape, setShape] = useState<KickLogoShape>("square");
    const [color, setColor] = useState<KickLogoColor>("black");
    const [isTransparent, setIsTransparent] = useState(false);
    const [size, setSize] = useState<KickLogoSize>("2xl");

    const format = toKickLogoFormat({ variant, shape });
    const logoProps = {
        ...kickLogoFormatProps[format],
        color,
        transparent: isTransparent,
        size,
    };
    const anatomy = useAnatomyMode({
        component: "kickLogo",
        variantId: kickLogoAnatomyVariantId({
            format,
            color,
            transparent: isTransparent,
        }),
        sizeId: size,
        isDisabled: false,
        kickLogoContent: { size },
    });

    const summary = `${format} · ${color} · ${isTransparent ? "transparent" : "opaque"} · ${kickLogoSizePixels[size]}px`;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Variant</h2>
                        <OptionToggle
                            options={kickLogoVariants}
                            value={variant}
                            onChange={setVariant}
                        />
                    </section>
                    <section>
                        <h2>Shape (icon only)</h2>
                        <OptionToggle
                            options={kickLogoShapes}
                            value={shape}
                            disabled={variant === "full"}
                            onChange={setShape}
                        />
                    </section>
                    <section>
                        <h2>Color</h2>
                        <OptionToggle
                            options={kickLogoColors}
                            value={color}
                            onChange={setColor}
                        />
                        <BooleanControl
                            label="Transparent"
                            checked={isTransparent}
                            onChange={setIsTransparent}
                        />
                    </section>
                    <section>
                        <h2>Size</h2>
                        <OptionToggle
                            options={kickLogoSizes}
                            value={size}
                            onChange={setSize}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={anatomy.isActive ? `x-ray · ${summary}` : summary}
                belowStage={
                    <>
                        <KickLogoVariantMatrix />
                        <KickLogoSizeMatrix />
                    </>
                }
                output={JSON.stringify({ KickLogo: logoProps }, null, 2)}
            >
                {anatomy.view ?? (
                    <span
                        className={styles.stageSurface}
                        data-surface={color === "white" ? "inverse" : "primary"}
                    >
                        <KickLogo {...logoProps} label="Kick" />
                    </span>
                )}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
